import Phaser from 'phaser';
import { MusicManager } from '../systems/MusicManager';
import { CampManager } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { ExpeditionManager } from '../systems/ExpeditionManager';
import { CodexManager } from '../systems/CodexManager';
import { RunManager } from '../systems/RunManager';
import { resetAllTutorials } from '../systems/TutorialResetter';
import { ParticleSettings } from '../rendering/ParticleEffects';
import { DisplayModeSettings, DisplayMode } from '../utils/DisplayModeSettings';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { PlayerProfile } from '../systems/PlayerProfile';
import { showUsernamePopup } from '../ui/UsernamePopup';
import { FONT_BODY } from '../utils/Typography';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import {
  buildSaveExport, serializeSaveExport, suggestExportFilename,
  parseSaveExport, applySaveImport,
} from '../systems/SaveExport';
import { downloadTextFile, pickTextFile } from '../utils/FileIO';
import { LifetimeStats } from '../systems/LifetimeStats';

// === SCÈNE DES PARAMÈTRES ===

const HEADER_H = 50;

export class SettingsScene extends Phaser.Scene {
  private returnScene = 'BootScene';
  private returnData: Record<string, unknown> = {};
  private panel!: ScrollablePanel;

  constructor() {
    super({ key: 'SettingsScene' });
  }

  init(data: { returnTo?: string; camp?: CampManager; meta?: MetaManager }): void {
    this.returnScene = data?.returnTo ?? 'BootScene';
    this.returnData = {};
    if (data?.camp) this.returnData.camp = data.camp;
    if (data?.meta) this.returnData.meta = data.meta;
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
  }

  private drawUI(): void {
    this.children.removeAll(true);
    this.panel?.destroy();
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e);

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);
    this.add.text(GAME_CX, 18, '⚙️ PARAMÈTRES', {
      fontSize: '22px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    const backBtn = this.add.text(15, 14, '← Retour', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 6, y: 3 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, this.returnScene, this.returnData));
    backBtn.on('pointerover', () => backBtn.setColor('#ff6b6b'));
    backBtn.on('pointerout', () => backBtn.setColor('#e74c3c'));

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);

    let y = 15;

    // Positions responsive : sur mobile, on stacke contrôle sous le label
    // (labelX, y+22) au lieu de côte-à-côte (labelX, ctrlBase). Évite le
    // débordement à droite (editBtn).
    const _mobSettings = GAME_W < 500;
    const labelX = Math.round(GAME_W * 0.08);
    const ctrlBase = _mobSettings ? labelX : Math.round(GAME_W * 0.58);
    const ctrlYOffset = _mobSettings ? 24 : 0;
    const lineExtra = _mobSettings ? 24 : 0;

    // === MUSIQUE ON/OFF ===
    const musicOn = MusicManager.isEnabled();
    this.panel.add(this.add.text(labelX, y, '🎵 Musique', {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }));
    const toggleBtn = this.add.text(ctrlBase, y + ctrlYOffset, musicOn ? '[ ON ]' : '[ OFF ]', {
      fontSize: '14px', color: musicOn ? '#2ecc71' : '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: musicOn ? '#1a2a1a' : '#2c1a1a', padding: { x: 12, y: 5 },
    });
    toggleBtn.setInteractive({ useHandCursor: true });
    toggleBtn.on('pointerdown', () => {
      MusicManager.setEnabled(!MusicManager.isEnabled());
      if (MusicManager.isEnabled()) {
        MusicManager.play(this, 'menu');
      }
      this.drawUI();
    });
    this.panel.add(toggleBtn);
    y += 50 + lineExtra;

    // === VOLUME MUSIQUE ===
    const musicVol = Math.round(MusicManager.getVolume() * 100);
    this.panel.add(this.add.text(labelX, y, '🔊 Volume musique', {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }));
    const volDown = this.add.text(ctrlBase, y + ctrlYOffset, '[-]', {
      fontSize: '14px', color: '#bdc3c7', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    });
    volDown.setInteractive({ useHandCursor: true });
    volDown.on('pointerdown', () => {
      MusicManager.setVolume(MusicManager.getVolume() - 0.1);
      this.drawUI();
    });
    this.panel.add(volDown);
    this.panel.add(this.add.text(ctrlBase + 50, y + ctrlYOffset + 2, `${musicVol}%`, {
      fontSize: '13px', color: '#f1c40f', fontFamily: FONT_BODY,
    }));
    const volUp = this.add.text(ctrlBase + 100, y + ctrlYOffset, '[+]', {
      fontSize: '14px', color: '#bdc3c7', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    });
    volUp.setInteractive({ useHandCursor: true });
    volUp.on('pointerdown', () => {
      MusicManager.setVolume(MusicManager.getVolume() + 0.1);
      this.drawUI();
    });
    this.panel.add(volUp);
    y += 50 + lineExtra;

    // === VOLUME SFX ===
    const sfxVol = Math.round(MusicManager.getSfxVolume() * 100);
    this.panel.add(this.add.text(labelX, y, '🔈 Volume SFX', {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }));
    const sfxDown = this.add.text(ctrlBase, y + ctrlYOffset, '[-]', {
      fontSize: '14px', color: '#bdc3c7', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    });
    sfxDown.setInteractive({ useHandCursor: true });
    sfxDown.on('pointerdown', () => {
      MusicManager.setSfxVolume(MusicManager.getSfxVolume() - 0.1);
      this.drawUI();
    });
    this.panel.add(sfxDown);
    this.panel.add(this.add.text(ctrlBase + 50, y + ctrlYOffset + 2, `${sfxVol}%`, {
      fontSize: '13px', color: '#f1c40f', fontFamily: FONT_BODY,
    }));
    const sfxUp = this.add.text(ctrlBase + 100, y + ctrlYOffset, '[+]', {
      fontSize: '14px', color: '#bdc3c7', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    });
    sfxUp.setInteractive({ useHandCursor: true });
    sfxUp.on('pointerdown', () => {
      MusicManager.setSfxVolume(MusicManager.getSfxVolume() + 0.1);
      this.drawUI();
    });
    this.panel.add(sfxUp);
    y += 70 + lineExtra;

    // === EFFETS VISUELS RÉDUITS ===
    const reduced = ParticleSettings.isReduced();
    this.panel.add(this.add.text(labelX, y, '✨ Effets visuels', {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }));
    const fxBtn = this.add.text(ctrlBase, y + ctrlYOffset, reduced ? '[ Réduits ]' : '[ Normaux ]', {
      fontSize: '14px', color: reduced ? '#e67e22' : '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: reduced ? '#2c2a1a' : '#1a2a1a', padding: { x: 12, y: 5 },
    });
    fxBtn.setInteractive({ useHandCursor: true });
    fxBtn.on('pointerdown', () => {
      ParticleSettings.toggle();
      this.drawUI();
    });
    this.panel.add(fxBtn);
    this.panel.add(this.add.text(labelX, y + 22 + ctrlYOffset, 'Désactive les particules en cas de ralentissements (mobile)', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      wordWrap: { width: GAME_W - 20 },
    }));
    y += 60 + lineExtra;

    // === MODE D'AFFICHAGE ===
    const currentMode = DisplayModeSettings.getMode();
    this.panel.add(this.add.text(labelX, y, '📱 Mode d\'affichage', {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }));

    const modes: { id: DisplayMode; label: string }[] = [
      { id: 'auto',    label: 'Auto' },
      { id: 'mobile',  label: 'Mobile' },
      { id: 'desktop', label: 'Bureau' },
    ];
    let modeBtnX = ctrlBase;
    for (const m of modes) {
      const isActive = currentMode === m.id;
      const btn = this.add.text(modeBtnX, y + ctrlYOffset, `[ ${m.label} ]`, {
        fontSize: '12px', color: isActive ? '#f1c40f' : '#bdc3c7', fontFamily: FONT_BODY,
        backgroundColor: isActive ? '#3a536b' : '#2c3e50', padding: { x: 6, y: 4 },
        fontStyle: isActive ? 'bold' : 'normal',
      });
      if (!isActive) {
        btn.setInteractive({ useHandCursor: true });
        btn.on('pointerdown', () => this.changeDisplayMode(m.id));
      }
      this.panel.add(btn);
      modeBtnX += btn.width + 4;
    }
    this.panel.add(this.add.text(labelX, y + 22 + ctrlYOffset, 'Auto = détection automatique. Recharge nécessaire pour appliquer.', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      wordWrap: { width: GAME_W - 20 },
    }));
    y += 60 + lineExtra;

    // === PSEUDO DU CAPITAINE ===
    const currentPseudo = PlayerProfile.getPseudo() ?? '(non défini)';
    this.panel.add(this.add.text(labelX, y, '⚓ Capitaine', {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }));
    this.panel.add(this.add.text(ctrlBase, y + ctrlYOffset, currentPseudo, {
      fontSize: '13px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }));
    const editBtnX = _mobSettings ? labelX + 140 : ctrlBase + 130;
    const editBtnY = _mobSettings ? y + ctrlYOffset : y;
    const editBtn = this.add.text(editBtnX, editBtnY, '[ Modifier ]', {
      fontSize: '11px', color: '#3498db', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 8, y: 4 },
    });
    editBtn.setInteractive({ useHandCursor: true });
    editBtn.on('pointerdown', async () => {
      const newPseudo = await showUsernamePopup();
      PlayerProfile.setPseudo(newPseudo);
      this.drawUI();
    });
    editBtn.on('pointerover', () => editBtn.setColor('#5dade2'));
    editBtn.on('pointerout', () => editBtn.setColor('#3498db'));
    this.panel.add(editBtn);
    this.panel.add(this.add.text(labelX, y + 22 + ctrlYOffset, 'Utilisé pour les bug reports et l\'écran d\'accueil', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      wordWrap: { width: GAME_W - 20 },
    }));
    y += 60 + lineExtra;

    // === SÉPARATEUR ===
    this.panel.add(this.add.rectangle(GAME_CX, y, Math.min(500, GAME_W - 40), 1, 0x333333));
    y += 25;

    // === SAUVEGARDE EXPORT / IMPORT ===
    // Mini "cloud save" pragmatique : le joueur télécharge un JSON de toute
    // sa progression et peut le réimporter sur un autre appareil. Pas de
    // backend nécessaire.
    this.panel.add(this.add.text(labelX, y, '💾 Sauvegarde', {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'bold',
    }));
    y += 22;

    const exportBtn = this.add.text(labelX, y, '[ ⬇️ Exporter ]', {
      fontSize: '12px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 10, y: 5 },
    });
    exportBtn.setInteractive({ useHandCursor: true });
    exportBtn.on('pointerdown', () => this.handleExport(exportBtn));
    exportBtn.on('pointerover', () => exportBtn.setColor('#27ae60'));
    exportBtn.on('pointerout', () => exportBtn.setColor('#2ecc71'));
    this.panel.add(exportBtn);

    // Import à droite du bouton Exporter (ou en dessous sur mobile très étroit)
    const importBtnX = _mobSettings ? labelX + 130 : labelX + 130;
    const importBtn = this.add.text(importBtnX, y, '[ ⬆️ Importer ]', {
      fontSize: '12px', color: '#3498db', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 10, y: 5 },
    });
    importBtn.setInteractive({ useHandCursor: true });
    importBtn.on('pointerdown', () => this.handleImport(importBtn));
    importBtn.on('pointerover', () => importBtn.setColor('#5dade2'));
    importBtn.on('pointerout', () => importBtn.setColor('#3498db'));
    this.panel.add(importBtn);

    y += 32;
    this.panel.add(this.add.text(labelX, y, 'Télécharge une copie complète de ta progression et restaure-la sur un autre appareil.', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      wordWrap: { width: GAME_W - labelX - 10 },
    }));
    y += _mobSettings ? 36 : 22;

    // === SÉPARATEUR ===
    this.panel.add(this.add.rectangle(GAME_CX, y, Math.min(500, GAME_W - 40), 1, 0x333333));
    y += 25;

    // === RESET ===
    const resetBtn = this.add.text(GAME_CX, y, '🗑️ Réinitialiser la partie', {
      fontSize: '13px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 12, y: 6 },
    }).setOrigin(0.5);
    resetBtn.setInteractive({ useHandCursor: true });
    resetBtn.on('pointerdown', () => {
      if (resetBtn.getData('confirm')) {
        new MetaManager().resetAll();
        new CampManager().resetAll();
        new CampManager().getResources().resetAll();
        new CampManager().getUpgrades().resetAll();
        new ExpeditionManager().resetAll();
        new CodexManager().resetAll();
        LifetimeStats.resetAll();
        resetAllTutorials();
        RunManager.clearSavedRun();
        try {
          localStorage.removeItem('deadtide_starter_given');
          localStorage.removeItem('deadtide_last_seen_version');
        } catch { /* */ }
        startSceneWithFade(this, 'BootScene');
      } else {
        resetBtn.setData('confirm', true);
        resetBtn.setText('⚠️ Confirmer la réinitialisation ?');
        this.time.delayedCall(3000, () => {
          if (resetBtn.active) {
            resetBtn.setData('confirm', false);
            resetBtn.setText('🗑️ Réinitialiser la partie');
          }
        });
      }
    });
    this.panel.add(resetBtn);
    this.panel.add(this.add.text(GAME_CX, y + 22, 'Efface toutes les données de jeu', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5));
    y += 55;

    // === SÉPARATEUR ===
    this.panel.add(this.add.rectangle(GAME_CX, y, Math.min(500, GAME_W - 40), 1, 0x333333));
    y += 25;

    // === CRÉDITS ===
    const creditsBtn = this.add.text(GAME_CX, y, '📋 Crédits', {
      fontSize: '14px', color: '#3498db', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 14, y: 6 },
    }).setOrigin(0.5);
    creditsBtn.setInteractive({ useHandCursor: true });
    creditsBtn.on('pointerdown', () => {
      startSceneWithFade(this, 'CreditsScene', { returnTo: 'SettingsScene', ...this.returnData });
    });
    this.panel.add(creditsBtn);
    y += 50;

    this.panel.setContentHeight(y + 20);
  }

  /**
   * Construit l'export et déclenche le téléchargement du fichier JSON.
   * Affiche un feedback temporaire sur le bouton.
   */
  private handleExport(btn: Phaser.GameObjects.Text): void {
    try {
      const data = buildSaveExport();
      const filename = suggestExportFilename(data);
      const content = serializeSaveExport(data);
      downloadTextFile(filename, content);
      this.flashButton(btn, '[ ✅ Exporté ]', '#2ecc71', '[ ⬇️ Exporter ]');
    } catch (e) {
      console.warn('[Settings] Export failed:', e);
      this.flashButton(btn, '[ ❌ Échec ]', '#e74c3c', '[ ⬇️ Exporter ]');
    }
  }

  /**
   * Ouvre le picker de fichier, valide le JSON et demande confirmation avant
   * d'écraser la save existante. Reload la page après succès pour repartir
   * sur un état propre (MetaManager + CampManager sont caches en mémoire).
   */
  private async handleImport(btn: Phaser.GameObjects.Text): Promise<void> {
    const raw = await pickTextFile();
    if (raw === null) return; // Utilisateur annulé

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.flashButton(btn, '[ ❌ JSON invalide ]', '#e74c3c', '[ ⬆️ Importer ]');
      return;
    }

    const validation = parseSaveExport(parsed);
    if (!validation.data) {
      console.warn('[Settings] Import validation failed:', validation.error);
      this.flashButton(btn, '[ ❌ Fichier invalide ]', '#e74c3c', '[ ⬆️ Importer ]');
      return;
    }

    // Confirmation double-clic via setData (même pattern que le reset)
    if (!btn.getData('confirmImport')) {
      btn.setData('confirmImport', validation.data);
      const origLabel = '[ ⬆️ Importer ]';
      btn.setText('[ ⚠️ Confirmer ? ]').setColor('#e67e22');
      this.time.delayedCall(4000, () => {
        if (btn.active && btn.getData('confirmImport')) {
          btn.setData('confirmImport', null);
          btn.setText(origLabel).setColor('#3498db');
        }
      });
      return;
    }

    // 2e clic : on applique
    const data = btn.getData('confirmImport');
    btn.setData('confirmImport', null);
    const result = applySaveImport(data);
    if (!result.ok) {
      this.flashButton(btn, '[ ❌ Échec ]', '#e74c3c', '[ ⬆️ Importer ]');
      return;
    }

    // Reload pour repartir sur un état propre — les managers cachent en mémoire
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.85).setDepth(300);
    this.add.text(GAME_CX, GAME_CY,
      `✅ Sauvegarde restaurée (${result.restored} clés)\nRechargement...`,
      { fontSize: '14px', color: '#2ecc71', fontFamily: FONT_BODY, align: 'center' }
    ).setOrigin(0.5).setDepth(301);
    this.time.delayedCall(700, () => {
      try { window.location.reload(); }
      catch { overlay.destroy(); }
    });
  }

  /** Affiche temporairement un texte/couleur de feedback sur un bouton. */
  private flashButton(btn: Phaser.GameObjects.Text, msg: string, color: string, restoreLabel: string): void {
    const restoreColor = btn.style.color as string;
    btn.setText(msg).setColor(color);
    this.time.delayedCall(2000, () => {
      if (btn.active) btn.setText(restoreLabel).setColor(restoreColor);
    });
  }

  /**
   * Change le mode d'affichage et recharge la page.
   * Reload nécessaire car GAME_W/GAME_H sont évalués à l'import du module
   * MobileHelper — impossible à modifier à chaud sans recréer toutes les scènes.
   */
  private changeDisplayMode(mode: DisplayMode): void {
    DisplayModeSettings.setMode(mode);

    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.85).setDepth(300);
    const msg = this.add.text(GAME_CX, GAME_CY,
      `Mode d'affichage changé.\nRechargement...`,
      { fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY, align: 'center' }
    ).setOrigin(0.5).setDepth(301);

    this.time.delayedCall(500, () => {
      try { window.location.reload(); }
      catch {
        overlay.destroy();
        msg.destroy();
      }
    });
  }

  shutdown(): void {
    this.panel?.destroy();
  }
}
